import React, { useState, useEffect, useCallback, JSX } from "react";
import { Box, Grid, Select, InputLabel, Tooltip, Typography, Divider, GridSize } from "@material-ui/core";
import MultiselectDropList, { MultiselectValue } from "../components/MultiselectDropList";
import Gridable from "../components/Gridable";
import Ellipsis from "../components/Ellipsis";
import NumberFormat from "react-number-format";
import translate from "../i18n/Translator";
import { PurchaseOrder } from "../model/PurchaseOrder";
import { DocumentLine } from "../model/Document";
import { getPurchaseOrder, getLines as getPurchaseOrderLines } from "../api/ProviderPurchaseOrderAPI";
import { getLines as getWarehouseDeliveryLines, getWarehouseDelivery, getRelatedCfdis as getWarehouseDeliveryRelatedCfdis } from "../api/ProviderWarehouseDeliveryAPI";
import ValidatedInput, { InputRef, isValid } from "../components/ValidatedInput";
import { GTEZeroValidator, LTEValidator } from "../components/Validators";
import { WarehouseDelivery } from "../model/WarehouseDelivery";
import { ShippingField, ShippingFieldDocumentLine } from "../model/ShippingData";
import { Cfdi, Cfdis } from "../model/Cfdi";
import { ErrorIcon } from "../components/Icons";

interface MultiselectValueIds extends MultiselectValue {
    id?: string
}

type DocumentType = "PURCHASE_ORDERS" | "WAREHOUSE_DELIVERIES";

interface DocumentTableItemsProps {
    limitTags?: number;
    title: string;
    elementos: MultiselectValueIds[];
    placement?: "end" | "start" | "top" | "bottom";
    margin?: "normal" | "dense" | "none";
    variant?: "filled" | "outlined" | "standard";
    required?: boolean;
    onChanged(lines: DocumentLine[], valid: boolean, warehouse_delivery_lines_read_only: boolean, isDisableSaveButton: boolean): void;
    type: DocumentType;
    disabled?: boolean;
    tenantId: string;
    providerId: string;
    cfdiId: string;
    payableDocument: Cfdi;
    isAllowMatchAmounts: boolean;
    messageAmountOutOfRange: string;
    toleranceDifference?: number;
}

interface LineQuantity {
    line: ShippingFieldDocumentLine,
    registeredQuantity: number
}

export default function DocumentTableItems(props: DocumentTableItemsProps) {
    const [status, setStatus] = useState<string>("loading");
    const [currentDocuments, setCurrentDocuments] = useState<string[]>([]);
    const [lines, setLines] = useState<DocumentLine[]>();
    const [linesQuantities, setLinesQuantities] = useState<LineQuantity[]>();
    const [currency, setCurrency] = useState<string>("");
    const [validations, setValidations] = useState<any>({});
    const [restrictToOneWarehouseDelivery, setRestrictToOneWarehouseDelivery] = useState<boolean>(false);
    const [warehouseDeliveryLinesReadOnly, setWarehouseDeliveryLinesReadOnly] = useState<boolean>(false);
    const [simpleSelectValue, setSimpleSelectValue] = useState<string>("");
    const [amountToAllocate, setAmountToAllocate] = useState<number>("CFDI" === props.payableDocument!.type ? props.payableDocument!.metadata.subtotal_base : props.payableDocument!.metadata.balance);
    const [calculatedAmount, setCalculatedAmount] = useState<number>(0);
    const [difference, setDifference] = useState<number>(Number((amountToAllocate - calculatedAmount).toFixed(6)));
    const isDifferenceOutOfRange = (calculatedDifference: number) => {
        const tolerance = props.toleranceDifference || 0.1;
        return (calculatedDifference < -tolerance || calculatedDifference > tolerance);
    }
    const [disableSaveButton, setDisableSaveButton] = useState<boolean>(props.isAllowMatchAmounts ? isDifferenceOutOfRange(difference) : false);

    const getPromise = () => {
        if (props.type === "PURCHASE_ORDERS") {
            return getPurchaseOrderLines(props.tenantId, props.providerId, props.cfdiId);
        }
        return getWarehouseDeliveryLines(props.tenantId, props.providerId, props.cfdiId);
    };

    const getInternalId = (externalId: string) => {
        let element = props.elementos.find(element => element.value === externalId);
        return element ? element.id : externalId;
    }

    const getPromisesByType = (identifiers : string []) => {
        return identifiers.map(identifier => {
            let id = getInternalId(identifier)
            if (props.type === "PURCHASE_ORDERS") {
                return getPurchaseOrder(props.tenantId, props.providerId, id!, 0, 0);
            }
            return getWarehouseDelivery(props.tenantId, props.providerId, id!);
        }) as Promise<PurchaseOrder | WarehouseDelivery>[];
    }

    useEffect(() => {
        if (!lines || lines.length === 0) {
            getPromise()
                .then((response) => {
                    setCurrentDocuments(response.document_ids);
                    const lins: DocumentLine[] = response.lines.filter(
                        (line) => line.status === undefined || line.status !== "C"
                    );
                    setLines(lins);
                    setCurrency(response.currency);
                    setRestrictToOneWarehouseDelivery(response.restrict_to_one_warehouse_delivery);
                    setWarehouseDeliveryLinesReadOnly(response.warehouse_delivery_lines_read_only);
                    if (response.restrict_to_one_warehouse_delivery && response.document_ids.length > 0) {
                        setSimpleSelectValue(response.document_ids[response.document_ids.length - 1]);
                    }
                    props.onChanged(
                        lins,
                        getValidation(response.warehouse_delivery_lines_read_only),
                        response.warehouse_delivery_lines_read_only,
                        disableSaveButton
                    );
                })
                .catch((error) => {
                    setStatus(error.message);
                });
        }
    }, [props]);

    const buildAmounts = (documentLines : DocumentLine[] | undefined) => {
        if(props.isAllowMatchAmounts) {
            let calculatedAmountTmp = documentLines && documentLines.length > 0 ? documentLines.map(line => line.quantity_in_cfdi ? Number(line.quantity_in_cfdi) * line.price : 0).reduce((total, amount) => total + amount, 0) : 0;
            let differenceTmp = Number((amountToAllocate - calculatedAmountTmp).toFixed(6));
            setCalculatedAmount(calculatedAmountTmp);
            setDifference(differenceTmp);
            setDisableSaveButton(isDifferenceOutOfRange(differenceTmp));
        }
    }

    const getOtherDocsQuantities = useCallback(async (documentsIds: string[], currentDocs: string[]) => {
        setLinesQuantities(undefined);
        const allQuantities = new Map();
        const processedCfdiLines = new Set<string>();
        const documents = await Promise.all(
            documentsIds.map(documentId => {
                if ("PURCHASE_ORDERS" === props.type) {
                    return getPurchaseOrder(props.tenantId, props.providerId, documentId, 0, 0);
                }
                return getWarehouseDeliveryRelatedCfdis(props.tenantId, props.providerId, documentId);
            })
        ).catch(error => {
            setStatus(error.message);
        });
        if (!documents) {
            return;
        }
        documents.forEach(doc => {
            let cfdis: Cfdi[] = [];
            if ("PURCHASE_ORDERS" === props.type) {
                cfdis = (doc as PurchaseOrder).cfdis || [];
            } else {
                cfdis = (doc as Cfdis).items || [];
            }
            cfdis.filter(cfdi => cfdi && cfdi.id !== props.cfdiId).forEach(cfdi => {
                const fields = Object.entries(cfdi.metadata.shipping_data?.fields || {});
                fields.forEach(([, field]: [string, ShippingField]) => {
                    field.lines?.forEach(line => {
                        if (!currentDocs.includes(line.external_id)) {
                            return;
                        }
                        const lineKey = `${cfdi.id}-${line.external_id}-${line.item_code}-${line.line_num}`;
                        if (processedCfdiLines.has(lineKey)) {
                            return;
                        };
                        processedCfdiLines.add(lineKey);
                        const itemKey = `${line.external_id}-${line.item_code}-${line.line_num}`;
                        const existingQuantity = allQuantities.get(itemKey) || { line, registeredQuantity: 0 };
                        existingQuantity.registeredQuantity += line.quantity_in_cfdi;
                        allQuantities.set(itemKey, existingQuantity);
                    });
                })
            });
        });
        setLinesQuantities(Array.from(allQuantities.values()));
    }, [props.cfdiId, props.providerId, props.tenantId, props.type]);

    useEffect(() => {
        if (lines && linesQuantities) {
            setStatus("loaded");
        }
    }, [lines, linesQuantities]);

    useEffect(() => {
        setValidations({});
        const documentsIds = props.elementos
            .filter((element) => currentDocuments.includes(element.value))
            .map((element) => element.id)
            .filter((id) => id !== undefined);
        (async () => getOtherDocsQuantities(documentsIds, currentDocuments))();
    }, [currentDocuments, getOtherDocsQuantities, props.elementos])

    useEffect(() => {
        if (currentDocuments && lines !== undefined) {
            const loadedExternalIds = new Set(lines.map((line) => line.external_id));

            const missingDocuments = currentDocuments.filter(
                (docId) => !loadedExternalIds.has(docId)
            );

            if (missingDocuments.length > 0) {
                setStatus("loading");
                const fetchPromises = getPromisesByType(missingDocuments);
                Promise.all(fetchPromises)
                    .then((documents) => {
                        const newLines = documents.flatMap((document) => {
                            return document.lines.map((line) => {
                                return {
                                    line_num: line.line_num,
                                    item_code: line.item_code,
                                    description: line.description,
                                    quantity: line.quantity,
                                    unit_of_measure: line.unit_of_measure,
                                    price: line.price,
                                    line_total: line.line_total,
                                    tax_code: line.tax_code,
                                    tax_rate: line.tax_rate,
                                    line_tax: line.line_tax,
                                    withholding_tax: line.withholding_tax,
                                    external_id: document.external_id,
                                    document_number: document.number
                                } as DocumentLine;
                            });
                        });
                          
                        setLines((prevLines) => (prevLines ? prevLines.concat(newLines) : newLines));
                    })
                    .catch((error) => {
                        setStatus(error.message);
                    });
            }
        }
    }, [lines, props.providerId, props.tenantId, props.type]);

    const getValidation = (warehouse_delivery_lines_read_only: boolean): boolean => {
        if (warehouse_delivery_lines_read_only) {
            return true;
        }
        return isValid(validations)
    }

    const onChange = (event: any) => {
        const { value: targetValue } = event.target;
        if (targetValue.trim() === '') {
            return;
        }
        setStatus("loading");
        setSimpleSelectValue(targetValue);
        addLines([targetValue], true);
    };

    const addLines = (selected: string[], isSimple: boolean) => {
        const added = selected.filter(el => currentDocuments.indexOf(el) < 0);
        if (added.length > 0) {
            const notAddedYetPromises = getPromisesByType(added);
            Promise.all(notAddedYetPromises).then(documents => {
                setCurrentDocuments(selected);
                const moreLines = documents.flatMap(document => {
                    return document.lines.map(line => {
                        return {
                            line_num: line.line_num,
                            item_code: line.item_code,
                            description: line.description,
                            quantity: line.quantity,
                            unit_of_measure: line.unit_of_measure,
                            price: line.price,
                            line_total: line.line_total,
                            tax_code: line.tax_code,
                            tax_rate: line.tax_rate,
                            line_tax: line.line_tax,
                            withholding_tax: line.withholding_tax,
                            external_id: document.external_id,
                            document_number: document.number
                        } as DocumentLine;
                    })
                });
                setLinesByDocuments(moreLines, isSimple);
            }).catch(error => {
                setStatus(error.message);
            });
        }
    };

    const onChanged = (selected: string[]) => {
        setStatus("loading");
        setCurrentDocuments(selected);
        const updatedLines = (lines || []).filter((line) => selected.includes(line.external_id));
        setLines(updatedLines);
        props.onChanged(updatedLines, getValidation(warehouseDeliveryLinesReadOnly), warehouseDeliveryLinesReadOnly, disableSaveButton);
    };

    const setLinesByDocuments = (moreLines: DocumentLine[], isSimple: boolean) => {
        const isValid = getValidation(warehouseDeliveryLinesReadOnly);
        if (!isSimple && lines && lines.length > 0) {
            const all = lines.concat(moreLines);
            setLines(all);
            props.onChanged(all ? all : [], isValid, warehouseDeliveryLinesReadOnly, disableSaveButton);
        } else {
            setLines(moreLines);
            props.onChanged(moreLines ? moreLines : [], isValid, warehouseDeliveryLinesReadOnly, disableSaveButton);
        }
        if (restrictToOneWarehouseDelivery && moreLines.length > 0) {
            setSimpleSelectValue(moreLines[0].external_id);
        }
    };

    const onChangeQuantity = (name: string, value: number, inputRef: InputRef) => {

        let temp = lines ? lines : [];
        temp.forEach(line => {
            const itemCode = `${line.external_id}_${line.item_code}_${line.line_num}`;
            if (itemCode === name) {
                line.quantity_in_cfdi = value;
            }
        });

        validations[name] = inputRef;
        setValidations(validations);

        setLines(temp);
        buildAmounts(temp);
        props.onChanged(lines ? lines : [], getValidation(warehouseDeliveryLinesReadOnly), warehouseDeliveryLinesReadOnly, disableSaveButton);
    };

    const getValues = () => {
        let values = {} as any;
        (lines || []).forEach(line => {
            values[line.external_id] = {
                title: line.document_number,
                value: line.external_id
            } as MultiselectValue;
        });

        let pos = props.elementos.filter(po => currentDocuments.includes(po.value));
        (pos || []).forEach(line => {
            values[line.value] = {
                title: line.title,
                value: line.value
            } as MultiselectValue;
        });

        return Object.values(values) as MultiselectValue[];
    };

    const buildAmountSection = (gridSize: boolean | GridSize, description : string, amountComponent: JSX.Element, amountGridStyle?: React.CSSProperties, tooltip?: JSX.Element) => {
        return (
            <Grid item xs={gridSize}>
                <Grid container>
                    <Grid item xs={12}>
                        <Grid container alignContent="flex-end" alignItems="flex-end" justifyContent="flex-end">
                            <Grid item>{description}</Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container alignContent="flex-end" alignItems="flex-end" justifyContent="flex-end">
                            {tooltip}
                            <Grid item style={amountGridStyle}>
                                {amountComponent}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid container justifyContent="center">
            <Grid item xs={12} sm={9} md={6} lg={6}>
                <Box pb={1} mt={-1}>
                    {restrictToOneWarehouseDelivery ?
                        <Grid item xs={12} sm={12} md={12} lg={12}>
                            <InputLabel htmlFor={"warehouseDeliverySelect"}>{props.title}</InputLabel>
                            <Select native style={{ width: "100%" }}
                                labelId="warehouseDeliverySelectId"
                                id="warehouseDeliverySelect"
                                value={simpleSelectValue}
                                onChange={onChange} >
                                <option key="---" value={undefined}>---</option>
                                {(props.elementos || []).map((element, index) => (
                                    <option key={element.value} value={element.value}>{element.title}</option>
                                ))}
                            </Select>
                        </Grid>
                        : <MultiselectDropList
                            title={props.title}
                            elementos={props.elementos}
                            value={getValues()}
                            required={props.required}
                            disabled={props.disabled}
                            onChanged={onChanged} />}
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box mx={-3}>
                    <Gridable
                        items={lines ? lines : []}
                        loading={status === "loading"}
                        empty={translate("providers.empty") as string}
                        columns={[
                            {
                                title: translate("purchase_orders.line_num") as string,
                                converter: (line) => line.line_num || "",
                                xs: false,
                                sm: false,
                                md: 1,
                                lg: 1,
                                xl: 1
                            },
                            {
                                title: translate(props.type === "PURCHASE_ORDERS" ? "purchase_orders.single" : "warehouse_deliveries.single") as string,
                                converter: (line) => line.external_id,
                                fullWidth: true,
                                xs: false,
                                sm: 1,
                                md: 1,
                                xl: 1
                            },
                            {
                                title: translate("purchase_orders.item_code") as string,
                                converter: (line) => line.item_code,
                                fullWidth: true,
                                xs: 3,
                                sm: 2,
                                md: 1,
                                xl: 1
                            },
                            {
                                title: translate("purchase_orders.unit_measure") as string,
                                converter: (line) => line.unit_of_measure,
                                xs: false,
                                sm: false,
                                md: 1,
                                xl: 1
                            },
                            {
                                title: translate("cfdis.details.description") as string,
                                converter: (line) => (
                                    <Ellipsis text={line.description} lenght={100} uppercased={false} />
                                ),
                                xs: false,
                                sm: 2,
                                md: 2,
                                xl: 2
                            },
                            {
                                title: translate("cfdis.details.quantity") as string,
                                style: { paddingLeft: "0px", paddingRight: "0px" },
                                converter: (line) => {
                                    const lineQuantity = linesQuantities?.find(
                                        (linesQuantity) =>
                                            linesQuantity.line.external_id === line.external_id
                                            && linesQuantity.line.item_code === line.item_code
                                            && linesQuantity.line.line_num === line.line_num
                                    );
                                    return <Grid style={{ paddingLeft: "0px" }}>
                                        {warehouseDeliveryLinesReadOnly ?
                                            <Ellipsis text={line.quantity.toString()} lenght={100} uppercased={false} />
                                            : <QuantityField
                                                itemCode={`${line.external_id}_${line.item_code}_${line.line_num}`}
                                                quantityPurchaseOrder={line.quantity}
                                                quantity={line.quantity_in_cfdi}
                                                quantityRegistered={lineQuantity?.registeredQuantity || 0}
                                                type={props.type}
                                                onChangeQuantity={onChangeQuantity} />
                                        }
                                    </Grid>
                                },
                                fullWidth: true,
                                xs: warehouseDeliveryLinesReadOnly ? 1 : 2,
                                sm: warehouseDeliveryLinesReadOnly ? 1 : 2,
                                md: warehouseDeliveryLinesReadOnly ? 1 : 2,
                                xl: warehouseDeliveryLinesReadOnly ? 1 : 2
                            },
                            {
                                title: translate("cfdis.details.unit_price", { "currency": currency }) as string,
                                justify: "center",
                                converter: (line) => (
                                    <NumberFormat value={line.price} prefix="$ " displayType="text" />
                                ),
                                xs: 3,
                                sm: 2,
                                md: 2,
                                xl: 2
                            },
                            {
                                title: translate("cfdis.details.price", { "currency": currency }) as string,
                                justify: "center",
                                converter: (line) => (
                                    <NumberFormat value={line.line_total} prefix="$ " displayType="text" />
                                ),
                                xs: 3,
                                sm: 2,
                                md: 2,
                                xl: 2
                            },
                        ]} />
                </Box>
            </Grid>
            {props.isAllowMatchAmounts && 
                <Grid item xs={12} justifyContent="center" style={{ paddingLeft: "0px" }}>
                    <Box px={2} py={2} paddingLeft={10} paddingRight={10} style={{ paddingLeft: "0px" }}>
                        <Grid container spacing={3} alignItems="center" justifyContent="center" style={{ paddingLeft: "0px" }}>
                            {buildAmountSection(true, translate("cfdis.popup.relate_odc_ea.amount_to_allocate") as string, <Typography color="primary"><NumberFormat value={amountToAllocate} prefix="$ "  decimalScale={6} fixedDecimalScale={false} thousandSeparator="," displayType="text" suffix={""} /></Typography>)}
                            <Divider orientation="vertical" flexItem id="uno"/>
                            {buildAmountSection(2, translate("cfdis.popup.relate_odc_ea.calcullated_amount") as string, <Typography color="secondary"><NumberFormat value={calculatedAmount} prefix="$ "  decimalScale={6} fixedDecimalScale={false} thousandSeparator="," displayType="text" suffix={""} /></Typography>)}
                            <Divider orientation="vertical" flexItem id="dos"/>
                            {buildAmountSection(2, translate("cfdis.popup.relate_odc_ea.difference") as string, <Typography ><NumberFormat value={difference} prefix="$ "  decimalScale={6} fixedDecimalScale={false} thousandSeparator="," displayType="text" suffix={""} /></Typography>, { "color": isDifferenceOutOfRange(difference) ? "red" : "green", fontWeight: "bolder" }, isDifferenceOutOfRange(difference) ?<Grid item><Tooltip title={props.messageAmountOutOfRange}><ErrorIcon htmlColor="red"/></Tooltip></Grid> : undefined)}
                        </Grid>
                    </Box>
                </Grid>
            }
        </Grid>
    );
}

interface QuantityFieldProps {
    itemCode: string;
    quantityPurchaseOrder: number;
    quantity?: number;
    quantityRegistered: number;
    type: DocumentType;
    onChangeQuantity(name: string, value: number, inputRef: InputRef): any;
}

function QuantityField(props: QuantityFieldProps) {
    let pendingQuantity = Number((props.quantityPurchaseOrder - props.quantityRegistered).toFixed(6));
    if (pendingQuantity < 0) {
        pendingQuantity = 0;
    }

    const gtZeroValidator = new GTEZeroValidator();
    const ltValidator = new LTEValidator(pendingQuantity);

    const translationKey = props.type === "PURCHASE_ORDERS" ? "purchase_orders" : "warehouse_deliveries";

    const pendings = `${translate(`${translationKey}.quantities.pending`) as string}: ${pendingQuantity}`;
    const registered = `${translate(`${translationKey}.quantities.registered`) as string}: ${props.quantityRegistered}`;
    const totals = `${translate(`${translationKey}.quantities.total`) as string}: ${props.quantityPurchaseOrder}`;

    return (
        <div>
            <Grid container justifyContent="center" alignContent="center">
                <Grid item xs={7} sm={7} md={7} lg={7} justifyContent="center" style={{ paddingLeft: "0px" }}>
                    <ValidatedInput
                        type="number"
                        id={props.itemCode}
                        name={props.itemCode}
                        value={props.quantity?.toString() || ""}
                        validators={[gtZeroValidator, ltValidator]}
                        label={""}
                        required={true}
                        margin="dense"
                        onValueChanged={props.onChangeQuantity}
                        style={{ paddingLeft: "9px" }}
                    />
                </Grid>
                <Grid item xs={4} sm={4} md={4} lg={4}>
                    <Box pt={2} pl={1}>
                        {"/" + pendingQuantity}
                    </Box>
                </Grid>
                <Grid item xs={1} sm={1} md={1} lg={1}>
                    <Box pt={3} ml={3} style={{ paddingTop: "15px", marginLeft: "10px" }}>
                        <Tooltip
                            title={
                                <Typography
                                    style={{ whiteSpace: "pre-line" }}
                                >{`${pendings}\n${registered}\n${totals}`}</Typography>
                            }
                            placement="right"
                        >
                            <span>
                                <ProgressDonut
                                    percentage={(props.quantityRegistered / props.quantityPurchaseOrder) * 100}
                                />
                            </span>
                        </Tooltip>
                    </Box>
                </Grid>
            </Grid>
        </div>
    );
}

interface ProgressDonutProps {
    percentage: number;
    pendingColor?: string;
    progressColor?: string;
    size?: number;
    thickness?: number;
}

function ProgressDonut({
    percentage,
    pendingColor = "#D1D1D1",
    progressColor = "#7CB381",
    size = 20,
    thickness = 10,
}: ProgressDonutProps) {
    const pendingPercentage = 100 - percentage;

    const degrees = (pendingPercentage / 100) * 360;

    let backgroundStyle;

    if (pendingPercentage === 0) {
        backgroundStyle = {
            backgroundColor: progressColor,
        };
    } else if (pendingPercentage === 100) {
        backgroundStyle = {
            backgroundColor: pendingColor,
        };
    } else {
        backgroundStyle = {
            background: `conic-gradient(${pendingColor} 0deg ${degrees}deg, ${progressColor} ${degrees}deg 360deg)`,
        };
    }

    const chartStyle = {
        ...backgroundStyle,
        width: size,
        height: size,
        borderRadius: "50%",
        position: "relative",
    } as React.CSSProperties;

    const holeStyle = {
        position: "absolute",
        top: thickness / 2,
        left: thickness / 2,
        width: size - thickness,
        height: size - thickness,
        borderRadius: "50%",
        background: "#fff",
    } as React.CSSProperties;

    return (
        <div style={chartStyle}>
            <div style={holeStyle}></div>
        </div>
    );
}
